import { useCallback, useEffect, useId, useMemo, useRef, useState } from "react";

import { useTranslate } from "@similarweb/i18n-provider";
import { colorsPalettes } from "@similarweb/styles";
import { Stack, Text } from "@similarweb/ui-components/foundations";

import type { ImpactedAssetGroup, IndustryTransitionImpactReport } from "../../types/impactReport";
import { impactedAssetGroupMap } from "../../types/impactReport";
import { useImpactReportTerritoryTeams } from "../../hooks/useImpactReportTerritoryTeams";
import { industryTransitionKeys } from "../../utils/i18n";

import { ImpactedAssetRow } from "./ImpactedAssetRow";
import { AssetListWrapper, StyledTab, StyledTabList, StyledTabPanel, StyledTabs } from "./styles";

type ImpactReportBodyProps = {
    readonly report: IndustryTransitionImpactReport;
};

type ExpandedByGroup = Partial<Record<ImpactedAssetGroup, ReadonlySet<string>>>;

const EMPTY_KEYS: ReadonlySet<string> = new Set();

/** Keyed by the asset's stable id, not its index, so an expanded row follows its asset across a refetch. */
const rowKeyOf = (group: ImpactedAssetGroup, assetId: string) => `${group}-${assetId}`;

/** The design opens the first row of each tab; a per-group default keeps that true on every tab. */
const defaultExpandedByGroup = (report: IndustryTransitionImpactReport): ExpandedByGroup =>
    Object.fromEntries(
        report.sections
            .filter((section) => section.assets.length > 0)
            .map((section) => [section.group, new Set([rowKeyOf(section.group, section.assets[0].id)])]),
    );

const toggleKey = (keys: ReadonlySet<string>, rowKey: string): ReadonlySet<string> => {
    const next = new Set(keys);

    if (!next.delete(rowKey)) {
        next.add(rowKey);
    }

    return next;
};

export const ImpactReportBody = ({ report }: ImpactReportBodyProps) => {
    const { translate } = useTranslate();
    const [activeIndex, setActiveIndex] = useState(0);
    const [expandedByGroup, setExpandedByGroup] = useState<ExpandedByGroup>({});
    const teamsById = useImpactReportTerritoryTeams(report);

    const tabListId = useId();
    const shouldFocusActiveTab = useRef(false);

    const defaultExpanded = useMemo(() => defaultExpandedByGroup(report), [report]);

    const activeGroup = report.sections[activeIndex].group;
    const expandedKeys = expandedByGroup[activeGroup] ?? defaultExpanded[activeGroup] ?? EMPTY_KEYS;

    const handleSelect = useCallback((index: number, _lastIndex: number, event: Event) => {
        shouldFocusActiveTab.current = event.type === "keydown";
        setActiveIndex(index);
    }, []);

    /**
     * The design-system `Tab` overwrites the ref react-tabs injects to track its own nodes, so the
     * library cannot move focus onto the tab it just selected. Reading the list back out of the DOM
     * is the only handle we have on those nodes; it has to wait for the render that makes the newly
     * selected tab focusable.
     */
    useEffect(() => {
        if (!shouldFocusActiveTab.current) {
            return;
        }

        shouldFocusActiveTab.current = false;
        document.getElementById(tabListId)?.querySelectorAll<HTMLElement>('[role="tab"]')[activeIndex]?.focus();
    }, [activeIndex, tabListId]);

    const handleToggleRow = useCallback(
        (group: ImpactedAssetGroup, rowKey: string) =>
            setExpandedByGroup((previous) => ({
                ...previous,
                [group]: toggleKey(previous[group] ?? defaultExpanded[group] ?? EMPTY_KEYS, rowKey),
            })),
        [defaultExpanded],
    );

    return (
        <StyledTabs
            selectedIndex={activeIndex}
            onSelect={handleSelect}
            disableUpDownKeys
        >
            <StyledTabList
                id={tabListId}
                data-automation="industry-transition-impact-report-tabs"
            >
                {report.sections.map((section) => (
                    <StyledTab
                        key={section.group}
                        data-automation={`industry-transition-impact-report-tab-${section.group}`}
                    >
                        <Stack gap="4px">
                            <Text
                                fontVariant="body2"
                                color={colorsPalettes.carbon[300]}
                            >
                                {translate(industryTransitionKeys.reportTab(section.group))}
                            </Text>
                            <Text
                                fontVariant="subtitle1"
                                color={colorsPalettes.carbon[500]}
                            >
                                {section.assets.length}
                            </Text>
                        </Stack>
                    </StyledTab>
                ))}
            </StyledTabList>

            {report.sections.map((section) => (
                <StyledTabPanel key={section.group}>
                    <AssetListWrapper
                        gap="8px"
                        data-automation={`industry-transition-impact-report-list-${section.group}`}
                    >
                        {section.assets.map((asset) => {
                            const rowKey = rowKeyOf(section.group, asset.id);

                            return (
                                <ImpactedAssetRow
                                    key={rowKey}
                                    group={section.group}
                                    rowKey={rowKey}
                                    asset={asset}
                                    isExpanded={expandedKeys.has(rowKey)}
                                    onToggle={handleToggleRow}
                                    dataAutomation={`industry-transition-impact-report-row-${rowKey}`}
                                    teamsById={
                                        section.group === impactedAssetGroupMap.territories ? teamsById : undefined
                                    }
                                />
                            );
                        })}
                    </AssetListWrapper>
                </StyledTabPanel>
            ))}
        </StyledTabs>
    );
};
